import type {IntlShape} from 'react-intl';
import {DEFAULT_ACCENT_COLOR} from './brandSwatches';
import {CURRENT_SCHEMA_VERSION, SECTION_TYPES, sectionOf} from '../persistence/quoteTemplateSchema';
import type {
	PlateBlockNode,
	PlateValue,
	QuoteDocumentData,
	QuoteSection,
	QuoteSectionType,
	QuoteTemplate,
} from '../resolver/types';

/**
 * The name the browser gives a locally downloaded PDF. The uploaded PDF keeps the server's own
 * naming convention, so this never reaches the stored quote.
 */
export const DEFAULT_FILE_NAME_TEMPLATE = 'quote_{reference}_{date}';

const EMPTY_PLATE: PlateValue = [{type: 'p', children: [{text: ''}]}];

/**
 * Blank-line separated text → one block per segment. A segment whose lines all begin with "- "
 * becomes a bulleted list, so a default can carry one without the copy having to be authored as a
 * tree. List text sits in a `lic` wrapper, which is where the editor puts it.
 */
export const textToParagraphs = (text: string): PlateValue => {
	const segments = text
		.split(/\n\n+/)
		.map(segment => segment.trim())
		.filter(Boolean);
	if (segments.length === 0) return EMPTY_PLATE;
	return segments.map((segment): PlateBlockNode => {
		const lines = segment.split('\n').map(line => line.trim());
		if (lines.length > 0 && lines.every(line => line.startsWith('- '))) {
			return {
				type: 'ul',
				children: lines.map(
					(line): PlateBlockNode => ({
						type: 'li',
						children: [{type: 'lic', children: [{text: line.slice(2).trim()}]}],
					})
				),
			};
		}
		return {type: 'p', children: [{text: segment.replace(/\n/g, ' ')}]};
	});
};

/**
 * Blank-line separated text → one term per segment: its first line as a bold lead-in paragraph,
 * the rest as the body, with a blank paragraph between terms.
 *
 * A bold lead-in rather than a heading block, because the design sets a term's name at body size
 * — a heading rendered it several times larger than the text it introduces. The blank paragraph
 * is what `parseTermsBlocks` reads as the break between one term and the next.
 */
export const textToTermBlocks = (text: string): PlateValue => {
	const blocks: PlateValue = [];
	for (const segment of text.split(/\n\n+/)) {
		const lines = segment
			.split('\n')
			.map(l => l.trim())
			.filter(Boolean);
		if (lines.length === 0) continue;
		if (blocks.length > 0) blocks.push({type: 'p', children: [{text: ''}]});
		blocks.push({type: 'p', children: [{text: lines[0], bold: true}]});
		if (lines.length > 1) {
			blocks.push({type: 'p', children: [{text: lines.slice(1).join(' ')}]});
		}
	}
	return blocks.length > 0 ? blocks : EMPTY_PLATE;
};

/** The message id of a section's built-in heading. Resolved at render time, never stored. */
export const defaultSectionTitleId = (type: QuoteSectionType): string => {
	switch (type) {
		case 'INTRODUCTION':
			return 'quote_designer.section.introduction';
		case 'SCOPE':
			return 'quote_designer.section.scope';
		default:
			return 'quote_designer.section.terms';
	}
};

/**
 * A section's heading as the reader sees it: the author's override when there is one, otherwise
 * the built-in heading in the reader's language.
 *
 * Titles are resolved here rather than stored, so a quote authored in one language still heads its
 * sections correctly when it is read in another — only an explicit rename is ever persisted.
 */
/**
 * Ceiling on a section heading. A heading is a label, not a paragraph: past this it stops being
 * readable as one and starts deciding the layout of the page it sits on.
 */
export const MAX_SECTION_TITLE_CHARS = 80;

/**
 * The longest run of characters with nowhere to break.
 *
 * Neither renderer will break inside a word, and react-pdf honours neither a zero-width space nor
 * a soft hyphen as an opportunity to - both were measured on a rendered PDF. So a run longer than
 * one line runs off the page however short the title is overall, and the run has to be cut too.
 * 24 characters fit a line at the largest heading size the themes use, measured the same way.
 */
const MAX_UNBROKEN_RUN_CHARS = 24;

const ELLIPSIS = '\u2026';

/** Clips an over-long heading to the ceiling, marking the cut so it does not read as the whole. */
export const truncateSectionTitle = (title: string): string => {
	const clipped =
		title.length > MAX_SECTION_TITLE_CHARS ? `${title.slice(0, MAX_SECTION_TITLE_CHARS - 1).trimEnd()}${ELLIPSIS}` : title;
	return clipped.replace(
		new RegExp(`\\S{${MAX_UNBROKEN_RUN_CHARS + 1},}`, 'g'),
		run => `${run.slice(0, MAX_UNBROKEN_RUN_CHARS - 1)}${ELLIPSIS}`
	);
};

/**
 * The heading a section renders under, for every surface: the panel's own label, the preview and
 * the PDF all resolve it here, so a stored title that predates the ceiling is clipped everywhere
 * rather than only where someone remembered to.
 */
export const sectionTitle = (template: QuoteTemplate, type: QuoteSectionType, intl: IntlShape): string => {
	const stored = sectionOf(template, type)?.title;
	return stored ? truncateSectionTitle(stored) : intl.formatMessage({id: defaultSectionTitleId(type)});
};

/**
 * The estimate, branding and layout a quote starts from. Built fresh on every call rather than
 * shared: two templates spreading one constant would alias the same nested objects, so toggling a
 * column on one would flip it on the other.
 */
const defaultEstimate = (): QuoteTemplate['estimate'] => ({
	rollup: 'roles',
	columns: {time: true, rate: true, amount: true},
	grouping: {roleTotals: true, phaseSubtotals: true, phaseExpenses: true, deliverableHeaders: true},
	totals: {subtotal: true, expenses: true, grandTotal: true},
});

const defaultBranding = (): QuoteTemplate['branding'] => ({
	accentColor: DEFAULT_ACCENT_COLOR,
	font: 'helvetica',
	showLogo: true,
});

const defaultLayout = (): QuoteTemplate['layout'] => ({theme: 'modern', pageHeader: true, pageFooter: true});

const section = (type: QuoteSectionType, body: PlateValue): QuoteSection => ({
	type,
	enabled: true,
	title: null,
	sourceBlockId: null,
	body,
});

/**
 * The template a new designer session starts from, before any account default is overlaid.
 * Narrative defaults are localized copy with the project / client names already interpolated, so
 * the very first preview renders sensibly.
 */
export const buildDefaultQuoteTemplate = (intl: IntlShape, data: QuoteDocumentData): QuoteTemplate => {
	const values = {
		projectName: data.projectName,
		clientName: data.clientName || data.projectName,
	};
	return {
		schemaVersion: CURRENT_SCHEMA_VERSION,
		branding: defaultBranding(),
		layout: defaultLayout(),
		estimate: defaultEstimate(),
		sections: [
			section('INTRODUCTION', textToParagraphs(intl.formatMessage({id: 'quote_designer.default.introduction'}, values))),
			section('SCOPE', textToParagraphs(intl.formatMessage({id: 'quote_designer.default.scope'}, values))),
			section('TERMS', textToTermBlocks(intl.formatMessage({id: 'quote_designer.default.terms'}))),
		],
	};
};

/** Structural fallback used only while the page is deciding to redirect away. */
export const buildEmptyQuoteTemplate = (): QuoteTemplate => ({
	schemaVersion: CURRENT_SCHEMA_VERSION,
	branding: defaultBranding(),
	layout: defaultLayout(),
	estimate: defaultEstimate(),
	sections: SECTION_TYPES.map(type => section(type, EMPTY_PLATE)),
});

export const EMPTY_QUOTE_TEMPLATE: QuoteTemplate = buildEmptyQuoteTemplate();
